import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import { parseValidationProblem } from '@/shared/lib/toast-helpers';
import type { ResourceSchedule } from '@/types/resource-schedule-api';

/**
 * Canonical React Query key for one device's schedule aggregate. Every write
 * under `api/Resource/{id}/Schedule` invalidates this key — there is no
 * per-section cache, the aggregate IS the cache.
 */
export function resourceScheduleQueryKey(
  resourceId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'resource-schedule', resourceId];
}

export function resourceScheduleUrl(resourceId: string): string {
  return `/api/Resource/${encodeURIComponent(resourceId)}/Schedule`;
}

/**
 * How the tab must react to a failed schedule read or write (PROD-2007
 * contract, same on every route):
 *
 *   validation  400 — `errors.DomainValidations[0]` is a user-facing sentence,
 *                     shown inline on the section
 *   archived    409 — the device was archived; the response `detail` is shown
 *                     and the tab becomes read-only (reads still work)
 *   gone        404 — the device (or the profile) no longer exists; close the
 *                     tab and refresh the registry list
 *   other           — anything else (5xx, network)
 */
export type ResourceScheduleErrorKind = 'validation' | 'archived' | 'gone' | 'other';

export class ResourceScheduleError extends Error {
  status: number;
  kind: ResourceScheduleErrorKind;

  constructor(message: string, status: number, kind: ResourceScheduleErrorKind) {
    super(message);
    this.name = 'ResourceScheduleError';
    this.status = status;
    this.kind = kind;
  }
}

export const ARCHIVED_FALLBACK_MESSAGE =
  'This device is archived. Restore it before editing its schedule.';
export const GONE_FALLBACK_MESSAGE = 'This device is no longer available.';

/** Loose ProblemDetails / ValidationProblemDetails reader. Never throws. */
function readProblem(body: string): { detail?: string; domainValidation?: string } {
  if (!body) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object') return {};
  const p = parsed as { detail?: unknown; errors?: unknown };
  const out: { detail?: string; domainValidation?: string } = {};
  if (typeof p.detail === 'string' && p.detail.trim()) out.detail = p.detail.trim();
  if (p.errors && typeof p.errors === 'object') {
    const dv = (p.errors as Record<string, unknown>).DomainValidations;
    if (Array.isArray(dv) && typeof dv[0] === 'string' && dv[0].trim()) {
      out.domainValidation = dv[0].trim();
    }
  }
  return out;
}

/** Short plain-text bodies are a message; JSON or long text is not. */
function plainText(body: string): string | null {
  const t = body?.trim() ?? '';
  return t && t.length < 200 && !t.startsWith('{') ? t : null;
}

/**
 * Map an `ApiError` from any Schedule route to a `ResourceScheduleError`.
 * Anything that is not a 400 / 404 / 409 (5xx, network, a 401 that survived
 * the refresh) is returned unchanged so the generic handling still applies.
 */
export function toResourceScheduleError(err: unknown): unknown {
  if (!(err instanceof ApiError)) return err;
  const problem = readProblem(err.body);
  if (err.status === 400) {
    const message =
      problem.domainValidation ??
      parseValidationProblem(err.body) ??
      'The schedule could not be saved.';
    return new ResourceScheduleError(message, 400, 'validation');
  }
  if (err.status === 409) {
    const message = problem.detail ?? plainText(err.body) ?? ARCHIVED_FALLBACK_MESSAGE;
    return new ResourceScheduleError(message, 409, 'archived');
  }
  if (err.status === 404) {
    const message = problem.detail ?? plainText(err.body) ?? GONE_FALLBACK_MESSAGE;
    return new ResourceScheduleError(message, 404, 'gone');
  }
  return err;
}

/** Narrowing helper for `onError` handlers and tests. */
export function isResourceScheduleError(
  err: unknown,
  kind?: ResourceScheduleErrorKind,
): err is ResourceScheduleError {
  return err instanceof ResourceScheduleError && (kind === undefined || err.kind === kind);
}

/**
 * The device's schedule — calendar settings, working hours, live appointment
 * profiles and the bookability verdict — in one call (PROD-2007).
 *
 *   GET /api/Resource/{resourceId}/Schedule
 *
 * The GET may create the settings row, which is why it travels with the
 * `UserId` header like every write (set by `api-client` from the auth store).
 * Errors are mapped so the tab can tell "gone" (close + refresh the registry)
 * from a transient failure. The global toast is opted out: the tab renders
 * its own inline state for each case.
 */
export function useResourceSchedule(resourceId: string | null | undefined) {
  return useQuery<ResourceSchedule, Error>({
    queryKey: resourceScheduleQueryKey(resourceId),
    queryFn: async ({ signal }) => {
      if (!resourceId) {
        throw new Error('useResourceSchedule called without resourceId');
      }
      try {
        return await get<ResourceSchedule>(resourceScheduleUrl(resourceId), { signal });
      } catch (err) {
        throw toResourceScheduleError(err) as Error;
      }
    },
    enabled: !!resourceId,
    staleTime: 30_000,
    // A 404 / 409 is a verdict, not a blip — retrying only delays the message.
    retry: (failureCount, err) =>
      !isResourceScheduleError(err, 'gone') &&
      !isResourceScheduleError(err, 'archived') &&
      failureCount < 1,
    meta: { skipGlobalError: true },
  });
}
